/**
 * PopoutComposeWindow
 *
 * Opens the full Outlook compose UI in a separate browser window by
 * navigating to /compose-window — a dedicated React route — instead of
 * rendering into a blank window.
 *
 * Why a route instead of ReactDOM.createRoot in a blank window?
 *   • Quill's Clipboard module, Ant Design portals, and other browser APIs
 *     all assume they're running in their own window context.  Injecting them
 *     into a foreign window document causes "Cannot read properties of null"
 *     errors and broken paste / prompt() behaviour.
 *   • A real page load gives every module the correct window / document
 *     references with no cross-window quirks.
 *
 * Data flow:
 *   1. Write { composeType, initialValues } to localStorage under a unique key
 *   2. Open  /compose-window?key=<key>  in a new window
 *   3. ComposeWindowPage reads + removes the key on mount
 *
 * This component renders null in the parent window and closes the popup
 * automatically when the user sends/discards inside it.
 */

import { useEffect, useRef } from 'react';

export default function PopoutComposeWindow({ composeType, initialValues, onClose }) {
  const timerRef = useRef(null);
  const popupRef = useRef(null);

  useEffect(() => {
    // ── 1. Store compose data so the popup page can read it ───────────────
    const key     = `bpa_compose_${Date.now()}`;
    const payload = JSON.stringify({ composeType, initialValues });
    localStorage.setItem(key, payload);

    // ── 2. Open the compose-window route ──────────────────────────────────
    const popup = window.open(
      `/compose-window?key=${key}`,
      `bpa_compose_${Date.now()}`,
      [
        'width=1100', 'height=780',
        'left=160',   'top=60',
        'menubar=no', 'toolbar=no', 'location=no',
        'status=no',  'resizable=yes', 'scrollbars=no',
      ].join(','),
    );

    if (!popup) {
      // Popup was blocked — clean up and notify parent
      localStorage.removeItem(key);
      onClose?.();
      return;
    }

    popupRef.current = popup;

    // ── 3. Poll for user-closed popup ─────────────────────────────────────
    timerRef.current = setInterval(() => {
      if (popup.closed) {
        clearInterval(timerRef.current);
        onClose?.();
      }
    }, 500);

    // ── 4. Cleanup when this component unmounts ───────────────────────────
    return () => {
      clearInterval(timerRef.current);
      try { if (!popup.closed) popup.close(); } catch (_) {}
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // mount once — props captured in closure

  return null;
}
